import React from 'react';
import { withSessionSsr, getPublicToken } from "../../lib/sessionMiddleware";
import { getCanonicalPath } from "../../lib/canonical";
import useHook from '../../hooks/pages/catalogo-mayorista/useHook';
import {
  Wrapper,
  TituloSeccion,
  Seccion,
  Footer,
  HeadTags,
  ItemProductoMayorista,
  Navbar,
  FiltrosTienda,
  SkeletonProducto,
  SinResultadosProductos,
} from '../../components';
import { currency } from '../../utils/utils';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.scss';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from 'next/link';

const CANTIDAD_SKELETONS = 8;

const CONDICIONES_MAYORISTA = [
  { titulo: 'Pedido mínimo', texto: '20 unidades combinadas como quieras. Los packs cuentan como una unidad.' },
  { titulo: 'Forma de pago', texto: 'Transferencia bancaria o Mercado Pago.' },
  { titulo: 'Envíos', texto: 'A cargo del comprador. Hacemos envíos por Andreani.' },
  { titulo: 'Tiempo de preparación', texto: 'De 3 a 5 días hábiles.' },
  { titulo: 'Personalización', texto: 'Los productos que llevan nombre, edad o textos personalizados se entregan sin personalizar en las compras mayoristas.' },
];

const MayoristaPage = (props) => {

  const tipos = props.pageProps.tipos;
  const tematicas = props.pageProps.tematicas;
  const colores = props.pageProps.colores;
  const productosIniciales = props.pageProps.productosIniciales;

  const {
    tipo,
    handleTipo,
    tematica,
    handleTematica,
    color,
    handleColor,
    destacado,
    handleDestacado,
    novedad,
    handleNovedad,
    handleClearFiltros,
    loadingProductos,
    loadingMore,
    productos,
    totalResults,
    sentinelRef,
    topRef,
    totalUnidades,
    totalPrecio,
    showCartButton,
  } = useHook({
    productos: productosIniciales,
    totalPages: props.pageProps.totalPagesIniciales,
    totalResults: props.pageProps.totalResultsIniciales,
  });

  const schema = productosIniciales?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": productosIniciales.map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "url": `${process.env.NEXT_PUBLIC_URL_FRONTEND}/productos/${p.attributes.slug}?mayorista=true`,
      "name": p.attributes.nombre,
    })),
  } : null;

  return (<>

    <HeadTags
      title="Catálogo Mayorista"
      description="Cotillón al por mayor para revendedores: pedido mínimo 20 unidades combinadas, con descuentos especiales."
      path={props.pageProps.pathname}
      schema={schema}
    />

    <Navbar/>

    <Seccion padding="40px 20px">
      <Wrapper>
        <TituloSeccion align="center">Catálogo Mayorista</TituloSeccion>
        <br/>

        <div className={styles.contenedorMayorista}>
          <Accordion
            sx={{
              boxShadow: 'none',
              border: 'none',
              '&::before': {
                display: 'none'
              },
              background: 'transparent'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
              style={{
                padding: "0px"
              }}
            >
              <span className={styles.tituloMayorista}>Condiciones generales</span>
            </AccordionSummary>
            <AccordionDetails style={{ padding: '14px 0 0' }}>

              <dl className={styles.listaCondiciones}>
                {CONDICIONES_MAYORISTA.map(c => (
                  <div className={styles.itemCondicion} key={c.titulo}>
                    <dt>{c.titulo}</dt>
                    <dd>{c.texto}</dd>
                  </div>
                ))}
              </dl>

              <div className={styles.comoPedir}>
                <p className={styles.comoPedirTitulo}>¿Cómo hacer tu pedido mayorista?</p>
                <p>Elegí los productos que querés de la siguiente lista y completá tus datos para enviar la solicitud del pedido. Una vez que la recibamos, te respondemos por WhatsApp o mail con:</p>
                <ul className={styles.pasos}>
                  <li>Costo de envío</li>
                  <li>Tiempo de producción</li>
                  <li>Los pasos para confirmar el pedido</li>
                </ul>
              </div>

            </AccordionDetails>
          </Accordion>
        </div>

        <div className={styles.contenedorCarrito}>
          {totalUnidades !== null &&
            <div className={styles.filaCarrito}>
              {!showCartButton ? (
                <p className={styles.textoFaltante}>
                  <span className={styles.corazon}>❤</span> Agregá <b>{20 - totalUnidades}</b> {20 - totalUnidades == 1 ? 'unidad' : 'unidades'} más.
                </p>
              ) : (
                <Link href="/carrito-mayorista">
                  <Button variant="contained">Finalizar pedido</Button>
                </Link>
              )}
              <p className={styles.textoTotal}>Total: <b>${currency(totalPrecio)}</b></p>
            </div>
          }
        </div>

        <div className={styles.layout}>

          <FiltrosTienda
            tipos={tipos}
            tematicas={tematicas}
            colores={colores}
            tipo={tipo}
            tematica={tematica}
            color={color}
            destacado={destacado}
            novedad={novedad}
            onChangeTipo={handleTipo}
            onChangeTematica={handleTematica}
            onChangeColor={handleColor}
            onToggleDestacado={handleDestacado}
            onToggleNovedad={handleNovedad}
            onClear={handleClearFiltros}
          />

          <div className={styles.content} ref={topRef}>

            {loadingProductos ?
              <div className={styles.contenedor}>
                {Array.from({ length: CANTIDAD_SKELETONS }).map((_, i) => (
                  <SkeletonProducto key={i} />
                ))}
              </div>
              :
              productos?.length > 0 ? <>
                <div className={styles.contenedor}>
                  {productos.map(item => (
                    <ItemProductoMayorista data={item} key={item.id} />
                  ))}
                </div>

                <div ref={sentinelRef} className={styles.sentinel} />

                {loadingMore &&
                  <div className={styles.loadingMore}>
                    <CircularProgress size={28} color="secondary" />
                  </div>
                }
              </>
                :
                <SinResultadosProductos
                  hasActiveFiltros={!!(tipo.length || tematica.length || color.length || destacado || novedad)}
                  onClear={handleClearFiltros}
                />
            }

          </div>

        </div>

      </Wrapper>
    </Seccion>

    <div className={styles.carritoSpacer} />

    <Footer />
  </>);
};

export default MayoristaPage;

export const getServerSideProps = withSessionSsr(
  async function getServerSideProps(ctx) {

    const { req, query } = ctx;

    const publicToken = await getPublicToken(req);

    const fetchTipos = async () => {
      try {
        const result = await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/tipos/get-filtered`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-spiral-secret': process.env.SPIRAL_SECRET
          },
        });
        let data = await result.json();
        return (data?.data || []);
      } catch (err) {
        console.log("e", err);
        return [];
      }
    };

    const fetchTematicas = async () => {
      try {
        const result = await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/tematicas/get-filtered`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-spiral-secret': process.env.SPIRAL_SECRET
          },
        });
        let data = await result.json();
        return (data?.data || []);
      } catch (err) {
        console.log("e", err);
        return [];
      }
    };

    const fetchColores = async () => {
      try {
        const result = await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/colores/get-filtered`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-spiral-secret': process.env.SPIRAL_SECRET
          },
        });
        let data = await result.json();
        return (data?.data || []);
      } catch (err) {
        console.log("e", err);
        return [];
      }
    };

    const fetchProductosIniciales = async () => {
      try {
        const body = {
          liviano: true,
          page: 1,
          pageSize: 24,
          tipo: (query.tipo || '').toString().trim(),
          tematica: (query.tematica || '').toString().trim(),
          color: (query.color || '').toString().trim(),
          destacado: query.destacado === '1',
          novedad: query.novedad === '1',
          aptoVentaMayorista: true,
        };
        const result = await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/productos/get-filtered`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-spiral-secret': process.env.SPIRAL_SECRET
          },
          body: JSON.stringify(body),
        });
        let data = await result.json();
        return {
          productos: data?.data || [],
          totalPages: Math.ceil((data?.meta?.pagination?.total || 0) / 24),
          totalResults: data?.meta?.pagination?.total || 0,
        };
      } catch (err) {
        console.log("e", err);
        return { productos: [], totalPages: 0, totalResults: 0 };
      }
    };

    let tematicas = await fetchTematicas();
    let tipos = await fetchTipos();
    let colores = await fetchColores();
    let productosIniciales = await fetchProductosIniciales();

    return {
      props: {
        pathname: getCanonicalPath(req),
        tematicas: tematicas,
        tipos: tipos,
        colores: colores,
        productosIniciales: productosIniciales.productos,
        totalPagesIniciales: productosIniciales.totalPages,
        totalResultsIniciales: productosIniciales.totalResults,
      },
    };

  },
);
